import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Container } from "../../components/ui/Container";
import { Stack } from "../../components/ui/Stack";
import { Heading } from "../../components/ui/Heading";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Spinner } from "../../components/ui/Spinner";
import { Alert } from "../../components/ui/Alert";
import { useApi } from "../../context/ApiContext";

interface Page {
 _id: string;
 title: string;
 slug: string;
 type?: "policy" | "static" | "custom";
 pageType?: "static" | "policy" | "product-detail" | "checkout" | "home" | "product-list" | "custom";
 category?: "cms" | "system";
 isActive: boolean;
 zones?: Record<string, any>;
 layout?: any;
 createdAt: string;
}

export const Pages: React.FC = () => {
 const { t } = useTranslation();
 const navigate = useNavigate();
 const { api } = useApi();
 const [pages, setPages] = useState<Page[]>([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState<string | null>(null);
 const [filterType, setFilterType] = useState("");
 const [filterActive, setFilterActive] = useState("");
 const [addingPages, setAddingPages] = useState(false);
 const [successMessage, setSuccessMessage] = useState<string | null>(null);

 useEffect(() => {
  fetchPages();
 }, [filterType, filterActive]);

 const fetchPages = async () => {
  try {
   setLoading(true);
   setError(null);
   
   // Fetch CMS pages - always include inactive to get all pages, then filter client-side
   const params = new URLSearchParams();
   if (filterType) params.append("type", filterType);
   params.append("includeInactive", "true"); // Always fetch all pages, filter client-side
   
   const cmsResponse = await api.get(`/pages?${params.toString()}`);
   const cmsPages = cmsResponse.data.pages || [];
   
   // Fetch system pages (page builder pages with zones)
   const pbParams = new URLSearchParams();
   pbParams.append("includeUnpublished", "true");
   pbParams.append("limit", "100");
   
   const pbResponse = await api.get(`/page-builder/pages?${pbParams.toString()}`);
   const pbPages = pbResponse.data.pages || [];
   
   // Merge pages, prioritizing CMS pages for duplicates
   const pageMap = new Map<string, Page>();
   
   // Add CMS pages first
   cmsPages.forEach((page: Page) => {
    pageMap.set(page._id, page);
   });
   
   // Add system pages (only if not already in map)
   pbPages.forEach((page: Page) => {
    if (!pageMap.has(page._id)) {
     pageMap.set(page._id, page);
    }
   });
   
   // Convert map to array and filter by type if needed
   let allPages = Array.from(pageMap.values());
   
   // Apply filters
   if (filterType) {
    allPages = allPages.filter((page) => {
     const pageType = page.type || page.pageType || "static";
     // Map "policy" to "static" for filtering
     const normalizedType = pageType === "policy" ? "static" : pageType;
     return normalizedType === filterType;
    });
   }
   
   if (filterActive) {
    // Filter by specific status
    if (filterActive === "true") {
     // Show only active pages
     allPages = allPages.filter((page) => page.isActive !== false);
    } else if (filterActive === "false") {
     // Show only inactive pages
     allPages = allPages.filter((page) => page.isActive === false);
    }
    // If filterActive has an unexpected value, show all pages
   }
   // If filterActive is empty ("All Status"), show all pages (no filtering)
   
   setPages(allPages);
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.pages.errors.fetch"));
  } finally {
   setLoading(false);
  }
 };

 const handleDelete = async (pageId: string) => {
  if (!window.confirm(t("admin.pages.confirmDelete"))) {
   return;
  }

  try {
   await api.delete(`/pages/${pageId}`);
   fetchPages();
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.pages.errors.delete"));
  }
 };

 const handleAddMissingPages = async () => {
  try {
   setAddingPages(true);
   setError(null);
   setSuccessMessage(null);
   const response = await api.post("/page-builder/pages/add-missing-default-pages");
   setSuccessMessage(response.data.message || t("admin.pages.pagesAdded"));
   fetchPages();
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.pages.errors.addPages"));
  } finally {
   setAddingPages(false);
  }
 };

 const getTypeLabel = (page: Page) => {
  const pageType = page.type || page.pageType || "static";
  // Map "policy" to "static" since policy pages are now static
  const normalizedType = pageType === "policy" ? "static" : pageType;
  const labels: Record<string, string> = {
   static: t("admin.pages.typeStatic"),
   custom: t("admin.pages.typeCustom"),
   home: "Home",
   "product-detail": "Product Detail",
   "product-list": "Product List",
   checkout: "Checkout",
  };
  return labels[normalizedType] || normalizedType;
 };
 
 const isSystemPage = (page: Page): boolean => {
  const pageType = page.pageType;
  return pageType === "home" || pageType === "checkout" || pageType === "product-detail" || pageType === "product-list";
 };

 if (loading && pages.length === 0) {
  return (
   <Container>
    <Stack align="center" spacing="lg">
     <Spinner size="lg" />
    </Stack>
   </Container>
  );
 }

 return (
  <Container>
   <Stack spacing="lg">
    <div className="flex items-center justify-between">
     <Heading size="xl">{t("admin.pages.title")}</Heading>
     <div className="flex gap-2">
      <Button 
       onClick={handleAddMissingPages} 
       disabled={addingPages}
       variant="outline"
      >
       {addingPages ? t("admin.pages.adding") : t("admin.pages.addMissingPages")}
      </Button>
      <Button onClick={() => navigate("/admin/pages/new")}>{t("admin.pages.addPage")}</Button>
     </div>
    </div>

    {error && <Alert variant="error">{error}</Alert>}
    {successMessage && <Alert variant="success">{successMessage}</Alert>}

    <Card padding="lg">
     <Stack spacing="md">
      <div className="flex gap-4 items-center">
       <div className="flex-1">
        <label className="block text-sm font-medium text-gray-700 mb-2">
         {t("admin.pages.filterByType")}
        </label>
        <select
         value={filterType}
         onChange={(e) => setFilterType(e.target.value)}
         className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
         <option value="">{t("admin.pages.allTypes")}</option>
         <option value="static">{t("admin.pages.typeStatic")}</option>
         <option value="custom">{t("admin.pages.typeCustom")}</option>
        </select>
       </div>
       <div className="flex-1">
        <label className="block text-sm font-medium text-gray-700 mb-2">
         {t("admin.pages.filterByStatus")}
        </label>
        <select
         value={filterActive}
         onChange={(e) => setFilterActive(e.target.value)}
         className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
         <option value="">{t("admin.pages.allStatus")}</option>
         <option value="true">{t("admin.active")}</option>
         <option value="false">{t("admin.inactive")}</option>
        </select>
       </div>
      </div>

      <Heading size="lg">{t("admin.pages.pagesList")}</Heading>
      {pages.length === 0 ? (
       <div className="text-center text-gray-500 py-8">
        {t("admin.pages.noPagesFound")}
       </div>
      ) : (
       <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
         <thead className="bg-gray-50">
          <tr>
           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {t("admin.pages.titleColumn")}
           </th>
           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {t("admin.pages.slug")}
           </th>
           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {t("admin.pages.type")}
           </th>
           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {t("admin.status")}
           </th>
           <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {t("admin.actions")}
           </th>
          </tr>
         </thead>
         <tbody className="bg-white divide-y divide-gray-200">
          {pages.map((page) => (
           <tr key={page._id}>
            <td className="px-6 py-4 whitespace-nowrap">
             <button
              onClick={() => navigate(`/admin/pages/${page._id}`)}
              className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left"
              type="button"
             >
              {page.title}
             </button>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
             <div className="text-sm text-gray-500">/{page.slug}</div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
             <span className="px-2 inline-flex text-xs leading-5 font-semibold bg-blue-100 text-blue-800">
              {getTypeLabel(page)}
             </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
             <span
              className={`px-2 inline-flex text-xs leading-5 font-semibold ${
               page.isActive
                ? "bg-green-100 text-green-800"
                : "bg-gray-100 text-gray-800"
              }`}
             >
              {page.isActive ? t("admin.active") : t("admin.inactive")}
             </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
             <div className="flex gap-2">
              <Button
               variant="outline"
               size="sm"
               onClick={() => navigate(`/admin/pages/${page._id}`)}
              >
               {t("admin.edit")}
              </Button>
              {!isSystemPage(page) && (
               <Button
                variant="outline"
                size="sm"
                onClick={() => handleDelete(page._id)}
                className="text-red-600 hover:text-red-700"
               >
                {t("admin.delete")}
               </Button>
              )}
             </div>
            </td>
           </tr>
          ))}
         </tbody>
        </table>
       </div>
      )}
     </Stack>
    </Card>
   </Stack>
  </Container>
 );
};

export default Pages;

